import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import confetti from 'canvas-confetti';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Progress } from './ui/progress';
import { 
    ArrowLeft, Plus, Calendar, Clock, Trash, CaretDown, CaretUp,
    FloppyDisk, Check, AirplaneTilt, Exam, PencilCircle, CalendarX, PencilSimple, Lightning
} from '@phosphor-icons/react';
import AddLessonModal from './AddLessonModal';
import LessonBudgetCard from './LessonBudgetCard';
import LessonBudgetFixModal from './LessonBudgetFixModal';
import SkillGrid from './SkillGrid';
import ProgressRing from './ProgressRing';
import BadgesPanel from './BadgesPanel';
import LevelBadge from './LevelBadge';
import ExamCountdown from './ExamCountdown';
import TrafficSketchTool, { SketchViewer } from './TrafficSketchTool';
import NextLessonCard from './NextLessonCard';
import RijschoolInfo from './RijschoolInfo';
import CancelLessonModal from './CancelLessonModal';
import EditStudentModal from './EditStudentModal';
import ExtraLessonsSlider, { FreeLessonsSlider } from './ExtraLessonsSlider';
import PaymentPlan from './PaymentPlan';
import CardCarousel from './CardCarousel';
import ReflectionTab from './ReflectionTab';
import { InlineSpinner } from './LoadingSpinner';
import AnimatedNumber from './AnimatedNumber';
import AnimatedProgressBar from './AnimatedProgressBar';
import { getPackageTheme } from '../utils/packageTheme';
import { StudentThemeProvider } from '../context/StudentThemeContext';
import BulkScoreModal from './BulkScoreModal';
import { computeEarnedBadgeIds } from '../utils/badges';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PACKAGE_INFO = {
    economy: { name: 'Economy Class', lessons: 25 },
    business: { name: 'Business Class', lessons: 35 },
    first_class: { name: 'First Class', lessons: 45 },
    spoedopleiding_5: { name: 'Spoedopleiding 5 uur', lessons: 5 },
    spoedopleiding_10: { name: 'Spoedopleiding 10 uur', lessons: 10 },
    spoedopleiding_16_5: { name: 'Spoedopleiding 16,5 uur', lessons: 16.5 },
    spoedopleiding_25: { name: 'Spoedopleiding 25 uur', lessons: 25 },
    spoedopleiding: { name: 'Spoedopleiding Nederland', lessons: 0 },
};

function getMotivationMessage(progress) {
    if (progress >= 100) return "Je bent een echte AutoPilot! Alle vaardigheden beheerst. Klaar voor het examen!";
    if (progress >= 90) return "Nog een klein stukje! Je bent bijna een volleerde bestuurder.";
    if (progress >= 80) return "Wat een prestatie! Je rijdt als een professional. De finish is in zicht!";
    if (progress >= 70) return "Expert niveau bereikt! Je beheerst het verkeer steeds beter.";
    if (progress >= 60) return "Over de helft! Je maakt echt grote stappen. Goed bezig!";
    if (progress >= 50) return "Halverwege! Je groeit met elke les. Blijf zo doorgaan!";
    if (progress >= 40) return "Je begint het echt onder de knie te krijgen. Sterk!";
    if (progress >= 30) return "Mooie voortgang! Je ontwikkelt je goed als bestuurder.";
    if (progress >= 20) return "Goed op weg! Elke les maakt je een betere rijder.";
    if (progress >= 10) return "Een goed begin is het halve werk. Je leert snel!";
    return "Welkom bij Rijschool AutoPilot! De reis naar je rijbewijs begint hier.";
}

export default function StudentLessonCard({ student, onBack, isInstructor }) {
    const [skills, setSkills] = useState([]);
    const [lessons, setLessons] = useState([]);
    const [showAddLesson, setShowAddLesson] = useState(false);
    const [loading, setLoading] = useState(true);
    const [expandedLesson, setExpandedLesson] = useState(null);
    const [budgetFixOpen, setBudgetFixOpen] = useState(false);
    const [currentStudent, setCurrentStudent] = useState(student);
    const [showSketchTool, setShowSketchTool] = useState(false);
    const [sketches, setSketches] = useState([]);
    const [viewingSketch, setViewingSketch] = useState(null);
    const [nextLesson, setNextLesson] = useState(null);
    const [scheduledLessons, setScheduledLessons] = useState([]);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showBulkModal, setShowBulkModal] = useState(false);

    // Editable state
    const [editedScores, setEditedScores] = useState({});
    const [editedNotes, setEditedNotes] = useState({});
    const [examDate, setExamDate] = useState('');
    const [examTime, setExamTime] = useState('');
    const [saving, setSaving] = useState(false);
    const [saveMessage, setSaveMessage] = useState('');
    const [hasChanges, setHasChanges] = useState(false);

    // Achievements tracking — earned + seen
    const [seenBadges, setSeenBadges] = useState(() =>
        Array.isArray(student.seen_badges) ? [...student.seen_badges] : []
    );
    const [activeTab, setActiveTab] = useState('leskaart');

    useEffect(() => {
        fetchData();
    }, [student.id]);

    // Listen for quick-reply driven actions coming from the chat modal
    useEffect(() => {
        if (isInstructor) return;
        // Note: 'qr:open-cancel' is handled globally in DashboardPage (opens the
        // bulk-cancel modal). We only listen for the scroll helpers here.
        const scrollTop = () => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        };
        window.addEventListener('qr:show-lessons', scrollTop);
        window.addEventListener('qr:show-payment', scrollTop);
        return () => {
            window.removeEventListener('qr:show-lessons', scrollTop);
            window.removeEventListener('qr:show-payment', scrollTop);
        };
    }, [isInstructor, nextLesson]);

    // Confetti when student opens leskaart and ALL skills are already mastered (4+).
    // Once per session per student to avoid spamming on every navigation.
    useEffect(() => {
        if (loading || skills.length === 0) return;
        const scores = currentStudent?.skill_scores || {};
        const allMastered = skills.every(s => (scores[s.id] || 0) >= 4);
        if (!allMastered) return;
        const sessionKey = `confetti_mastered_${currentStudent.id}`;
        if (sessionStorage.getItem(sessionKey)) return;
        sessionStorage.setItem(sessionKey, '1');
        triggerConfetti();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, skills, currentStudent.id]);

    const fetchData = async () => {
        try {
            const [skillsRes, lessonsRes, studentRes, sketchesRes, scheduledRes] = await Promise.all([
                axios.get(`${API}/skills`, { withCredentials: true }),
                axios.get(`${API}/lessons?student_id=${student.id}`, { withCredentials: true }),
                axios.get(`${API}/students/${student.id}`, { withCredentials: true }),
                axios.get(`${API}/sketches?student_id=${student.id}`, { withCredentials: true }),
                axios.get(`${API}/scheduled-lessons`, { withCredentials: true })
            ]);
            setSkills(skillsRes.data);
            setLessons(lessonsRes.data);
            setCurrentStudent(studentRes.data);
            setEditedScores(studentRes.data.skill_scores || {});
            setEditedNotes(studentRes.data.skill_notes || {});
            setExamDate(studentRes.data.exam_date || '');
            setExamTime(studentRes.data.exam_time || '');
            setSketches(sketchesRes.data);
            
            // Find next upcoming lesson for this student (exclude lessons that have already ended)
            const now = new Date();
            const studentScheduled = (scheduledRes.data || []).filter(l => l.student_id === student.id);
            setScheduledLessons(studentScheduled);
            const upcoming = studentScheduled
                .filter(l => {
                    try {
                        const end = new Date(`${l.date}T${l.time || '09:00'}:00`);
                        end.setMinutes(end.getMinutes() + (l.duration_minutes || 60));
                        return end > now;
                    } catch { return false; }
                })
                .sort((a, b) => a.date.localeCompare(b.date) || (a.time || '').localeCompare(b.time || ''));
            setNextLesson(upcoming[0] || null);
        } catch (e) {
            console.error('Error fetching data:', e);
        } finally {
            setLoading(false);
        }
    };

    const handleLessonAdded = () => {
        setShowAddLesson(false);
        fetchData();
    };

    const handleDeleteLesson = async (lessonId) => {
        if (!window.confirm('Weet je zeker dat je deze les wilt verwijderen?')) return;
        try {
            await axios.delete(`${API}/lessons/${lessonId}`, { withCredentials: true });
            fetchData();
        } catch (e) {
            console.error('Error deleting lesson:', e);
        }
    };

    const handleScoreChange = useCallback((skillId, score) => {
        setEditedScores(prev => ({ ...prev, [skillId]: score }));
        setHasChanges(true);
    }, []);

    const handleNoteChange = useCallback((skillId, note) => {
        setEditedNotes(prev => ({ ...prev, [skillId]: note }));
        setHasChanges(true);
    }, []);

    const handleSave = async () => {
        setSaving(true);
        setSaveMessage('');
        try {
            // Save scores + notes
            await axios.put(`${API}/students/${student.id}/skills`, {
                skill_scores: editedScores,
                skill_notes: editedNotes
            }, { withCredentials: true });

            // Save exam date + time if changed
            if (examDate !== (currentStudent.exam_date || '') || examTime !== (currentStudent.exam_time || '')) {
                await axios.put(`${API}/students/${student.id}`, {
                    exam_date: examDate,
                    exam_time: examTime
                }, { withCredentials: true });
            }

            setCurrentStudent(prev => ({ ...prev, skill_scores: editedScores, skill_notes: editedNotes, exam_date: examDate, exam_time: examTime }));
            setHasChanges(false);
            setSaveMessage('Opgeslagen!');
            setTimeout(() => setSaveMessage(''), 3000);

            // Confetti if all skills are mastered!
            const allMastered = skills.length > 0 && skills.every(s => (editedScores[s.id] || 0) >= 4);
            if (allMastered) {
                triggerConfetti();
            }
        } catch (e) {
            console.error('Error saving:', e);
            setSaveMessage('Fout bij opslaan');
        } finally {
            setSaving(false);
        }
    };

    // Overall progress = average score across all skills, as % (0-100).
    // Uses `skills.length` as denominator so unscored skills count as 0 —
    // means every score adjustment moves the number.
    const getOverallProgress = () => {
        if (!skills || skills.length === 0) return 0;
        const sum = skills.reduce((acc, s) => acc + (Number(editedScores[s.id]) || 0), 0);
        return Math.round((sum / (skills.length * 5)) * 100);
    };

    const getCategoryProgress = (category) => {
        const categorySkills = skills.filter(s => s.category === category);
        if (categorySkills.length === 0) return 0;
        const sum = categorySkills.reduce((acc, s) => acc + (Number(editedScores[s.id]) || 0), 0);
        return Math.round((sum / (categorySkills.length * 5)) * 100);
    };

    // Lessons DRIVEN (registered + past scheduled). We deliberately DO NOT add
    // initial_lessons here — initial_lessons reduces the purchased package instead
    // (see LessonBudgetCard) so it never inflates the "gereden" counter.
    const lessonsUsed = (() => {
        const byDate = {};
        const now = new Date();
        const addEntry = (date, durationMin) => {
            if (!date) return;
            const hours = (durationMin || 60) / 60;
            if (!byDate[date] || byDate[date] < hours) byDate[date] = hours;
        };
        lessons.forEach(l => addEntry(l.date, l.duration_minutes));
        scheduledLessons.forEach(l => {
            if (l.status === 'cancelled') return;
            try {
                const end = new Date(`${l.date}T${l.time || '09:00'}:00`);
                end.setMinutes(end.getMinutes() + (l.duration_minutes || 60));
                if (end <= now) addEntry(l.date, l.duration_minutes);
            } catch (e) { /* skip */ }
        });
        const total = Object.values(byDate).reduce((a, b) => a + b, 0);
        return Math.round(total * 10) / 10;
    })();

    // Lessons that are already scheduled (planned) but not yet driven (future).
    // These count toward the "actief" total so the denominator auto-grows when
    // instructor plans lessons beyond the purchased amount.
    const lessonsPlanned = (() => {
        const byDate = {};
        const now = new Date();
        scheduledLessons.forEach(l => {
            if (l.status === 'cancelled') return;
            try {
                const end = new Date(`${l.date}T${l.time || '09:00'}:00`);
                end.setMinutes(end.getMinutes() + (l.duration_minutes || 60));
                if (end > now) {
                    const hours = (l.duration_minutes || 60) / 60;
                    if (!byDate[l.date] || byDate[l.date] < hours) byDate[l.date] = hours;
                }
            } catch (e) { /* skip */ }
        });
        return Math.round(Object.values(byDate).reduce((a, b) => a + b, 0) * 10) / 10;
    })();

    // Earned achievement badges based on lessons used + skill scores
    const earnedBadges = useMemo(
        () => computeEarnedBadgeIds({ skills, scores: editedScores, lessonCount: Math.floor(lessonsUsed) }),
        [skills, editedScores, lessonsUsed]
    );
    const unseenBadgeCount = earnedBadges.filter(id => !seenBadges.includes(id)).length;

    // When the student opens the achievements tab, mark all currently-earned badges as seen.
    useEffect(() => {
        if (activeTab !== 'badges' || isInstructor) return;
        if (earnedBadges.length === 0) return;
        const missing = earnedBadges.filter(id => !seenBadges.includes(id));
        if (missing.length === 0) return;
        const next = Array.from(new Set([...seenBadges, ...earnedBadges]));
        setSeenBadges(next);
        axios.post(`${API}/students/${student.id}/seen-badges`, { seen_badges: next }, { withCredentials: true })
            .catch(() => { /* tolerate transient errors — local state already updated */ });
    }, [activeTab, earnedBadges, seenBadges, isInstructor, student.id]);

    const categories = [...new Set(skills.map(s => s.category))];
    const categoryNames = {
        bediening_voertuig: 'Bediening Voertuig',
        verkeersdeelneming: 'Verkeersdeelneming',
        bijzondere_verrichtingen: 'Bijzondere Verrichtingen'
    };

    // Combined lesson history: registered lessons + past scheduled lessons.
    // Dedup per date (registered lesson wins because it has notes / assessments).
    const historyEntries = (() => {
        const byKey = new Map();
        const addRegistered = (l) => {
            const key = `${l.date}|${l.time || ''}`;
            byKey.set(key, {
                key,
                id: l.id,
                source: 'registered',
                date: l.date,
                time: l.time || null,
                duration_minutes: l.duration_minutes || 60,
                notes: l.notes || '',
                assessments: l.assessments || [],
            });
        };
        const addScheduled = (l) => {
            // Only past scheduled lessons; skip cancelled
            if (l.status === 'cancelled') return;
            try {
                const end = new Date(`${l.date}T${l.time || '09:00'}:00`);
                end.setMinutes(end.getMinutes() + (l.duration_minutes || 60));
                if (end > new Date()) return;
            } catch { return; }

            // If we already have a registered lesson on this exact date+time, keep it.
            const exactKey = `${l.date}|${l.time || ''}`;
            if (byKey.has(exactKey)) return;
            // If there's already a registered entry that day and no time was set on the scheduled one,
            // treat as duplicate.
            const sameDay = Array.from(byKey.values()).find(e => e.date === l.date && e.source === 'registered');
            if (sameDay && !l.time) return;

            byKey.set(exactKey || `${l.date}|scheduled-${l.id}`, {
                key: exactKey,
                id: l.id,
                source: 'scheduled',
                date: l.date,
                time: l.time || null,
                duration_minutes: l.duration_minutes || 60,
                notes: l.notes || '',
                assessments: [],
                confirmation_status: l.confirmation_status,
                driven_check: !!l.driven_check,
            });
        };
        lessons.forEach(addRegistered);
        scheduledLessons.forEach(addScheduled);
        return Array.from(byKey.values()).sort((a, b) => (
            b.date.localeCompare(a.date) || (b.time || '').localeCompare(a.time || '')
        ));
    })();

    const pkg = PACKAGE_INFO[currentStudent.package_type];
    const extraLessons = currentStudent.extra_lessons || 0;
    const freeLessons = currentStudent.free_lessons || 0;
    const initialLessons = parseFloat(currentStudent.initial_lessons) || 0;
    // Package cap is reduced by "initial_lessons" (transfer/pre-existing lessons)
    // so a student who came in with 10 already-done pkg lessons + 7 extras shows a total of 7.
    const pkgAfterInitial = pkg ? Math.max(0, pkg.lessons - initialLessons) : 0;
    const totalPackageLessons = pkgAfterInitial + extraLessons + freeLessons;
    const theme = getPackageTheme(currentStudent.package_type);
    // Pricing: Spoedopleiding = €58/uur, everything else = €60/uur. External = no price.
    const pkgType = currentStudent.package_type || '';
    const isExternal = !!currentStudent.external;
    const pricePerLesson = isExternal
        ? null
        : pkgType.startsWith('spoedopleiding') ? 58 : 60;

    const triggerConfetti = () => {
        const duration = 3000;
        const end = Date.now() + duration;
        const colors = ['#D4A537', '#E5B84A', '#FFFFFF', '#38A169'];

        (function frame() {
            confetti({ particleCount: 3, angle: 60, spread: 55, origin: { x: 0 }, colors });
            confetti({ particleCount: 3, angle: 120, spread: 55, origin: { x: 1 }, colors });
            if (Date.now() < end) requestAnimationFrame(frame);
        })();
    };

    // Loading state: render nothing so opening the leskaart feels instant
    // (the user explicitly asked to remove the loading animation).
    if (loading) {
        return null;
    }

    // Map theme primary to a stable key for CSS scoping
    const themeKey = (() => {
        const p = currentStudent.package_type;
        if (p === 'economy') return 'economy';
        if (p === 'business') return 'business';
        if (p === 'first_class') return 'first_class';
        return 'default';
    })();

    return (
        <StudentThemeProvider value={theme}>
        <div className="student-themed" data-student-theme={themeKey}>
            <div className="flex items-center gap-4 mb-6">
                <Button variant="ghost" onClick={onBack} className="text-gray-400 hover:text-white hover:bg-[#3D4A5C]" data-testid="back-to-list-button">
                    <ArrowLeft size={20} className="mr-2" />
                    Terug
                </Button>
                <Button
                    variant="outline"
                    onClick={() => setShowEditModal(true)}
                    className="ml-auto border-[#4A5568] text-gray-300 hover:bg-[#3D4A5C] hover:text-white"
                    data-testid="edit-student-button"
                >
                    <PencilSimple size={16} className="mr-2" />
                    Wijzig gegevens
                </Button>
                {isInstructor && (
                    <Button
                        variant="outline"
                        onClick={() => setShowBulkModal(true)}
                        className="border-[#D4A537]/50 text-[#D4A537] hover:bg-[#D4A537]/10 hover:text-[#E5B84A]"
                        data-testid="bulk-scores-button"
                    >
                        <Lightning size={16} weight="fill" className="mr-2" />
                        Snel scores
                    </Button>
                )}
            </div>

            {/* Student Info Card */}
            <Card
                className="bg-[#2D3748] mb-6"
                style={{
                    borderColor: theme.primary + '55',
                    boxShadow: `0 0 32px -6px ${theme.auraSoft}, inset 0 0 0 1px ${theme.primary}22`,
                }}
            >
                <CardContent className="pt-6">
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                        <div>
                            <h2 className="text-2xl font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                {currentStudent.name}
                            </h2>
                            <p className="text-gray-400 mt-1">{currentStudent.email}</p>
                            {currentStudent.phone && <p className="text-gray-400">{currentStudent.phone}</p>}
                            
                            {/* Package badge */}
                            {pkg && (
                                <div className="mt-2 flex items-center gap-2 flex-wrap">
                                    <span className={`flex items-center gap-1.5 px-3 py-1 border rounded-full text-sm font-medium ${theme.pillClass}`}>
                                        <AirplaneTilt size={16} weight="fill" />
                                        {pkg.name}
                                    </span>
                                </div>
                            )}

                            {/* Lesson budget overview */}
                            <div className="mt-3">
                                <LessonBudgetCard
                                    packageName={pkg?.name}
                                    packageLessons={pkgAfterInitial}
                                    extraLessons={extraLessons}
                                    freeLessons={freeLessons}
                                    driven={lessonsUsed}
                                    planned={lessonsPlanned}
                                    onCorrect={isInstructor ? () => setBudgetFixOpen(true) : undefined}
                                />
                            </div>

                            {/* Exam date + time */}
                            {isInstructor && (
                                <div className="mt-3 flex items-center gap-3">
                                    <Label className="text-gray-400 text-sm flex items-center gap-1">
                                        <Exam size={16} className="text-[#D4A537]" />
                                        Examen:
                                    </Label>
                                    <Input
                                        type="date"
                                        value={examDate}
                                        onChange={(e) => { setExamDate(e.target.value); setHasChanges(true); }}
                                        className="w-40 h-8 bg-[#1A202C] border-[#4A5568] text-white text-sm"
                                        data-testid="exam-date-input"
                                    />
                                    <Input
                                        type="time"
                                        value={examTime}
                                        onChange={(e) => { setExamTime(e.target.value); setHasChanges(true); }}
                                        className="w-28 h-8 bg-[#1A202C] border-[#4A5568] text-white text-sm"
                                        placeholder="09:00"
                                        data-testid="exam-time-input"
                                    />
                                </div>
                            )}
                            {!isInstructor && currentStudent.exam_date && (
                                <div className="mt-3 flex items-center gap-2 text-sm">
                                    <Exam size={16} className="text-[#D4A537]" />
                                    <span className="text-gray-400">Examen:</span>
                                    <span className="text-white font-medium">
                                        {currentStudent.exam_date}
                                        {currentStudent.exam_time && ` om ${currentStudent.exam_time}`}
                                    </span>
                                </div>
                            )}

                            {/* Level Badge (small) */}
                            <div className="mt-3">
                                <LevelBadge progress={getOverallProgress()} size="small" />
                            </div>
                        </div>

                        <div className="flex items-center gap-6">
                            {/* Progress Ring — denominator auto-expands to reflect the biggest of
                                bought / (driven + planned). Keeps 3/7 → 8/8 flow coherent. */}
                            {(() => {
                                const displayTotal = Math.max(totalPackageLessons || 0, lessonsUsed + lessonsPlanned);
                                if (displayTotal > 0) {
                                    return (
                                        <ProgressRing
                                            value={lessonsUsed}
                                            max={displayTotal}
                                            size={100}
                                            strokeWidth={8}
                                            label="Lessen"
                                            sublabel={`${Math.max(0, Math.round((displayTotal - lessonsUsed) * 10) / 10)} resterend`}
                                        />
                                    );
                                }
                                return (
                                    <div className="text-center">
                                        <div className="text-3xl font-bold text-white">
                                            <AnimatedNumber value={lessonsUsed} duration={1100} />
                                        </div>
                                        <div className="text-sm text-gray-400">Lessen</div>
                                    </div>
                                );
                            })()}
                            <div className="text-center">
                                <div className={`text-3xl font-bold ${theme.textClass}`}>
                                    <AnimatedNumber value={getOverallProgress()} duration={1400} suffix="%" />
                                </div>
                                <div className="text-sm text-gray-400">Voortgang</div>
                            </div>
                        </div>
                        
                        {/* Motivatie bericht */}
                        <div className="mt-3 px-4 py-2.5 bg-[#D4A537]/10 border border-[#D4A537]/30 rounded-lg">
                            <p className="text-sm text-[#D4A537] font-medium italic text-center">
                                {getMotivationMessage(getOverallProgress())}
                            </p>
                        </div>
                    </div>

                    {/* Lesson package progress bar — moved into <LessonBudgetCard/> in the header */}

                    {/* Extra lessen + Gratis lessen schuifbalken (alleen instructeur, niet voor externe leerlingen) */}
                    {isInstructor && !isExternal && (
                        <>
                            <ExtraLessonsSlider
                                studentId={currentStudent.id}
                                initialValue={extraLessons}
                                pricePerLesson={pricePerLesson}
                                onSaved={(newVal) => {
                                    setCurrentStudent(prev => ({ ...prev, extra_lessons: newVal }));
                                }}
                            />
                            <FreeLessonsSlider
                                studentId={currentStudent.id}
                                initialValue={freeLessons}
                                onSaved={(newVal) => {
                                    setCurrentStudent(prev => ({ ...prev, free_lessons: newVal }));
                                }}
                            />
                        </>
                    )}
                    
                    {/* Category Progress */}
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                        {categories.map(cat => {
                            const pct = getCategoryProgress(cat);
                            return (
                                <div key={cat} className="bg-[#1A202C] rounded-lg p-3 border border-[#4A5568]">
                                    <div className="text-xs text-gray-400 mb-1">{categoryNames[cat] || cat}</div>
                                    <AnimatedProgressBar
                                        pct={pct}
                                        barClassName="bg-gradient-to-r from-[#D4A537] via-[#FFD96A] to-[#D4A537]"
                                        height={6}
                                        duration={1500}
                                        shimmer
                                        showPercent
                                        labelClassName="text-xs font-medium text-white tabular-nums w-10 text-right"
                                    />
                                </div>
                            );
                        })}
                    </div>

                    {/* Carousel (Level / Volgende les / Annuleren / Betaling / Examen) */}
                    <div className="mt-4 flex justify-start">
                        <CardCarousel
                            autoRotateMs={6000}
                            slides={[
                                {
                                    id: 'level',
                                    label: 'Level',
                                    visible: true,
                                    content: (
                                        <LevelBadge progress={getOverallProgress()} />
                                    ),
                                },
                                {
                                    id: 'next-lesson',
                                    label: 'Volgende les',
                                    visible: !!nextLesson,
                                    content: nextLesson ? (
                                        <div className="space-y-2">
                                            <NextLessonCard lesson={nextLesson} isInstructor={isInstructor} onConfirmed={() => setNextLesson(prev => prev ? { ...prev, confirmation_status: 'confirmed' } : prev)} />
                                        </div>
                                    ) : null,
                                },
                                {
                                    id: 'cancel-lesson',
                                    label: 'Annuleren',
                                    visible: !isInstructor && !!nextLesson,
                                    content: (
                                        <Card className="bg-[#2D3748] border-red-500/40 h-full">
                                            <CardContent className="py-5 flex flex-col items-center justify-center gap-3 text-center min-h-[140px]">
                                                <CalendarX size={32} className="text-red-400" weight="fill" />
                                                <div>
                                                    <h3 className="text-white font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Les annuleren</h3>
                                                    <p className="text-xs text-gray-400 mt-1">Niet beschikbaar voor je geplande les?</p>
                                                </div>
                                                <Button
                                                    onClick={() => setShowCancelModal(true)}
                                                    size="sm"
                                                    className="bg-red-500/20 border border-red-500/40 text-red-200 hover:bg-red-500/30"
                                                    data-testid="cancel-lesson-button"
                                                >
                                                    <CalendarX size={14} className="mr-1.5" />
                                                    Les annuleren
                                                </Button>
                                            </CardContent>
                                        </Card>
                                    ),
                                },
                                {
                                    id: 'payment-plan',
                                    label: 'Betaling',
                                    visible: isInstructor || !!currentStudent.payment_total,
                                    content: (
                                        <PaymentPlan
                                            student={currentStudent}
                                            totalLessons={totalPackageLessons}
                                            lessonsUsed={lessonsUsed}
                                            isInstructor={isInstructor}
                                            onUpdate={(updated) => setCurrentStudent(prev => ({ ...prev, ...updated }))}
                                        />
                                    ),
                                },
                                {
                                    id: 'exam-countdown',
                                    label: 'Examen',
                                    visible: !!(examDate || currentStudent.exam_date),
                                    content: (
                                        <ExamCountdown
                                            examDate={examDate || currentStudent.exam_date}
                                            examTime={examTime || currentStudent.exam_time}
                                            examTableNumber={currentStudent.exam_table_number}
                                            lessonsUsed={lessonsUsed}
                                            lessonsTotal={totalPackageLessons || 0}
                                        />
                                    ),
                                },
                            ]}
                        />
                    </div>
                </CardContent>
            </Card>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
                <div className="flex justify-between items-center">
                    <TabsList className="bg-[#2D3748] border border-[#4A5568]">
                        <TabsTrigger value="leskaart" data-testid="tab-leskaart" className="data-[state=active]:bg-[#D4A537] data-[state=active]:text-[#1A202C]">
                            Leskaart
                        </TabsTrigger>
                        <TabsTrigger value="lessen" data-testid="tab-lessen" className="data-[state=active]:bg-[#D4A537] data-[state=active]:text-[#1A202C]">
                            Lesgeschiedenis
                        </TabsTrigger>
                        <TabsTrigger value="info" data-testid="tab-info" className="data-[state=active]:bg-[#D4A537] data-[state=active]:text-[#1A202C]">
                            Informatie Rijschool
                        </TabsTrigger>
                        <TabsTrigger value="reflectie" data-testid="tab-reflectie" className="data-[state=active]:bg-[#D4A537] data-[state=active]:text-[#1A202C]">
                            Zelfreflectie
                        </TabsTrigger>
                        <TabsTrigger value="badges" data-testid="tab-badges" className="relative data-[state=active]:bg-[#D4A537] data-[state=active]:text-[#1A202C]">
                            Achievements
                            {unseenBadgeCount > 0 && (
                                <span
                                    className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none animate-pulse"
                                    data-testid="badges-unseen-counter"
                                    aria-label={`${unseenBadgeCount} nieuwe achievements`}
                                >
                                    {unseenBadgeCount > 9 ? '9+' : unseenBadgeCount}
                                </span>
                            )}
                        </TabsTrigger>
                        <TabsTrigger value="schetsen" data-testid="tab-schetsen" className="data-[state=active]:bg-[#D4A537] data-[state=active]:text-[#1A202C]">
                            Schetsen
                        </TabsTrigger>
                    </TabsList>
                    <div className="flex items-center gap-3">
                        {saveMessage && (
                            <span className={`text-sm flex items-center gap-1 ${saveMessage === 'Opgeslagen!' ? 'text-emerald-400' : 'text-red-400'}`}>
                                {saveMessage === 'Opgeslagen!' && <Check size={16} />}
                                {saveMessage}
                            </span>
                        )}
                        {isInstructor && hasChanges && (
                            <Button size="sm" onClick={handleSave} disabled={saving} className="bg-[#D4A537] text-[#1A202C] hover:bg-[#E5B84A] font-semibold" data-testid="save-scores-button">
                                <FloppyDisk size={16} className="mr-1" />
                                {saving ? 'Opslaan...' : 'Opslaan'}
                            </Button>
                        )}
                        {isInstructor && (
                            <Button size="sm" onClick={() => setShowSketchTool(true)} variant="outline" className="border-[#4A5568] text-gray-300 hover:bg-[#3D4A5C]" data-testid="open-sketch-button">
                                <PencilCircle size={18} className="mr-1" />
                                Schets
                            </Button>
                        )}
                        {isInstructor && (
                            <Button onClick={() => setShowAddLesson(true)} className="bg-[#D4A537] text-[#1A202C] hover:bg-[#E5B84A] font-semibold" data-testid="add-lesson-button">
                                <Plus size={18} className="mr-2" />
                                Nieuwe Les
                            </Button>
                        )}
                    </div>
                </div>

                <TabsContent value="leskaart">
                    <Card className="bg-[#2D3748] border-[#4A5568]">
                        <CardHeader>
                            <div className="flex justify-between items-center">
                                <CardTitle className="text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                    Digitale Leskaart
                                </CardTitle>
                                {isInstructor && (
                                    <p className="text-sm text-gray-500 italic">Klik op een score om aan te passen</p>
                                )}
                            </div>
                        </CardHeader>
                        <CardContent>
                            <SkillGrid 
                                skills={skills}
                                skillScores={editedScores}
                                skillNotes={editedNotes}
                                categories={categories}
                                categoryNames={categoryNames}
                                editable={isInstructor}
                                onScoreChange={handleScoreChange}
                                onNoteChange={handleNoteChange}
                            />
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="lessen">
                    <Card className="bg-[#2D3748] border-[#4A5568]">
                        <CardHeader>
                            <div className="flex items-center justify-between flex-wrap gap-2">
                                <CardTitle className="text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Lesgeschiedenis</CardTitle>
                                <div className="flex items-center gap-2 text-xs">
                                    <span className="px-2 py-0.5 rounded-md bg-[#1A202C] border border-[#4A5568] text-gray-300 tabular-nums">
                                        {historyEntries.length} lessen totaal
                                    </span>
                                    <span className="px-2 py-0.5 rounded-md bg-[#1A202C] border border-[#4A5568] text-gray-300 tabular-nums">
                                        {(historyEntries.reduce((s, e) => s + (e.duration_minutes || 60), 0) / 60).toFixed(1)} uur
                                    </span>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent>
                            {historyEntries.length === 0 ? (
                                <div className="text-center py-12 text-gray-400">Nog geen lessen geregistreerd</div>
                            ) : (
                                <div className="space-y-3">
                                    {historyEntries.map(entry => {
                                        const isScheduled = entry.source === 'scheduled';
                                        return (
                                        <div key={`${entry.source}-${entry.id}`} className="border border-[#4A5568] rounded-lg overflow-hidden" data-testid={`lesson-item-${entry.id}`}>
                                            <div className="p-4 flex items-center justify-between cursor-pointer hover:bg-[#3D4A5C]" onClick={() => setExpandedLesson(expandedLesson === entry.id ? null : entry.id)}>
                                                <div className="flex items-center gap-4 flex-wrap">
                                                    <div className="flex items-center gap-2 text-gray-300">
                                                        <Calendar size={18} className="text-[#D4A537]" />
                                                        <span className="font-medium tabular-nums">{entry.date}</span>
                                                        {entry.time && <span className="text-gray-400 tabular-nums">om {entry.time}</span>}
                                                    </div>
                                                    <div className="flex items-center gap-2 text-gray-400">
                                                        <Clock size={16} />
                                                        <span className="tabular-nums">{entry.duration_minutes} min</span>
                                                    </div>
                                                    {isScheduled ? (
                                                        <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border border-[#4A5568] bg-[#1A202C] text-gray-400" title="Uit de agenda (geen scoring toegevoegd)">
                                                            Uit agenda
                                                        </span>
                                                    ) : (
                                                        <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border border-[#D4A537]/40 bg-[#D4A537]/10 text-[#E5B84A]">
                                                            Beoordeeld
                                                        </span>
                                                    )}
                                                    {entry.assessments?.length > 0 && (
                                                        <span className="text-sm text-gray-500">{entry.assessments.length} beoordelingen</span>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {isInstructor && !isScheduled && (
                                                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleDeleteLesson(entry.id); }} className="text-gray-500 hover:text-red-400 hover:bg-red-900/20" data-testid={`delete-lesson-${entry.id}`}>
                                                            <Trash size={16} />
                                                        </Button>
                                                    )}
                                                    {expandedLesson === entry.id ? <CaretUp size={20} className="text-gray-500" /> : <CaretDown size={20} className="text-gray-500" />}
                                                </div>
                                            </div>
                                            {expandedLesson === entry.id && (
                                                <div className="border-t border-[#4A5568] p-4 bg-[#1A202C]">
                                                    {entry.notes && (
                                                        <div className="mb-4">
                                                            <div className="text-sm font-medium text-gray-300 mb-1">Notities</div>
                                                            <p className="text-gray-400">{entry.notes}</p>
                                                        </div>
                                                    )}
                                                    {entry.assessments?.length > 0 && (
                                                        <div>
                                                            <div className="text-sm font-medium text-gray-300 mb-2">Beoordelingen</div>
                                                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                                                                {entry.assessments.map((a, idx) => {
                                                                    const skill = skills.find(s => s.id === a.skill_id);
                                                                    return (
                                                                        <div key={idx} className={`p-2 rounded border grade-${a.score}`}>
                                                                            <div className="text-sm font-medium">{skill?.name || a.skill_id}</div>
                                                                            <div className="text-lg font-bold">{a.score}/5</div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    )}
                                                    {isScheduled && !entry.notes && (
                                                        <p className="text-xs text-gray-500 italic">Deze les komt uit de agenda en heeft nog geen beoordelingen. Voeg een geregistreerde les toe om scores en notities vast te leggen.</p>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        );
                                    })}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="info">
                    <div className="max-w-2xl">
                        <RijschoolInfo />
                    </div>
                </TabsContent>

                <TabsContent value="reflectie">
                    <ReflectionTab studentId={student.id} readOnly={isInstructor} />
                </TabsContent>

                <TabsContent value="badges">
                    <Card className="bg-[#2D3748] border-[#4A5568]">
                        <CardContent className="pt-6">
                            <BadgesPanel 
                                skills={skills}
                                scores={editedScores}
                                lessonCount={Math.floor(lessonsUsed)}
                            />
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="schetsen">
                    <Card className="bg-[#2D3748] border-[#4A5568]">
                        <CardHeader className="flex flex-row items-center justify-between">
                            <CardTitle className="text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                Situatieschetsen
                            </CardTitle>
                            {isInstructor && (
                                <Button size="sm" onClick={() => setShowSketchTool(true)} className="bg-[#D4A537] text-[#1A202C] hover:bg-[#E5B84A] font-semibold">
                                    <Plus size={16} className="mr-1" /> Nieuwe Schets
                                </Button>
                            )}
                        </CardHeader>
                        <CardContent>
                            {sketches.length === 0 ? (
                                <div className="text-center py-12 text-gray-400">
                                    <PencilCircle size={48} className="mx-auto mb-4 opacity-50" />
                                    <p>Nog geen situatieschetsen</p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                    {sketches.map(sketch => (
                                        <div
                                            key={sketch.id}
                                            onClick={() => setViewingSketch(sketch)}
                                            className="bg-[#1A202C] border border-[#4A5568] rounded-lg p-3 cursor-pointer hover:border-[#D4A537] transition-all group"
                                            data-testid={`sketch-${sketch.id}`}
                                        >
                                            <div className="text-sm font-medium text-white mb-1">{sketch.title}</div>
                                            <div className="text-xs text-gray-500">{sketch.background}</div>
                                            <div className="text-xs text-gray-500 mt-1">{(sketch.elements || []).length} elementen</div>
                                            {isInstructor && (
                                                <Button
                                                    variant="ghost" size="sm"
                                                    onClick={async (e) => {
                                                        e.stopPropagation();
                                                        await axios.delete(`${API}/sketches/${sketch.id}`, { withCredentials: true });
                                                        fetchData();
                                                    }}
                                                    className="mt-2 text-gray-500 hover:text-red-400 hover:bg-red-900/20 h-6 px-2 opacity-0 group-hover:opacity-100"
                                                >
                                                    <Trash size={14} className="mr-1" /> Verwijderen
                                                </Button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            {showAddLesson && <AddLessonModal studentId={student.id} skills={skills} onClose={() => setShowAddLesson(false)} onSuccess={handleLessonAdded} />}
            {showSketchTool && <TrafficSketchTool studentId={student.id} onClose={() => setShowSketchTool(false)} onSave={fetchData} />}
            {viewingSketch && <SketchViewer sketch={viewingSketch} onClose={() => setViewingSketch(null)} />}
            {showCancelModal && (
                <CancelLessonModal
                    lesson={nextLesson}
                    studentName={currentStudent.name}
                    onClose={() => setShowCancelModal(false)}
                />
            )}
            {showEditModal && (
                <EditStudentModal
                    student={currentStudent}
                    studentMode={!isInstructor}
                    onClose={() => setShowEditModal(false)}
                    onSuccess={() => {
                        setShowEditModal(false);
                        fetchData();
                    }}
                />
            )}
            {isInstructor && (
                <BulkScoreModal
                    open={showBulkModal}
                    onClose={() => setShowBulkModal(false)}
                    skills={skills}
                    currentScores={editedScores}
                    onApply={(newScores) => {
                        setEditedScores(newScores);
                        setHasChanges(true);
                    }}
                />
            )}
            {isInstructor && (
                <LessonBudgetFixModal
                    open={budgetFixOpen}
                    onClose={() => setBudgetFixOpen(false)}
                    studentId={student.id}
                    studentName={currentStudent.name}
                    initialDriven={lessonsUsed}
                    initialTotal={totalPackageLessons}
                    onSaved={() => {
                        // Refetch only the local leskaart data — do NOT bubble up to
                        // the dashboard's fetchData (which resets the selected view
                        // back to Home on mobile).
                        setBudgetFixOpen(false);
                        (async () => {
                            try {
                                const [studentRes, lessonsRes, scheduledRes] = await Promise.all([
                                    axios.get(`${API}/students/${student.id}`, { withCredentials: true }),
                                    axios.get(`${API}/lessons?student_id=${student.id}`, { withCredentials: true }),
                                    axios.get(`${API}/scheduled-lessons?student_id=${student.id}`, { withCredentials: true }).catch(() => ({ data: [] })),
                                ]);
                                setCurrentStudent(studentRes.data);
                                setLessons(lessonsRes.data || []);
                                setScheduledLessons(scheduledRes.data || []);
                            } catch (e) {
                                // best-effort — fall back to full refetch if needed
                                fetchData();
                            }
                        })();
                    }}
                />
            )}
        </div>
        </StudentThemeProvider>
    );
}
